import { useState, useCallback } from "react"
import { User, Follow } from "../ZeefTypes"
import ZeefService from "../ZeefService"

interface SocialLoadingState {
  followers: boolean
  following: boolean
  mutual: boolean
}

interface SocialCursors {
  followers: { page: number; hasMore: boolean }
  following: { page: number; hasMore: boolean }
  mutual: { page: number; hasMore: boolean }
}

interface UseZeefSocialReturn {
  // State
  followers: User[]
  following: User[]
  mutualFollows: User[]
  onlineUsers: Set<string>
  loading: SocialLoadingState
  cursors: SocialCursors
  searchQuery: string
  searchResults: User[]
  isSearching: boolean
  isFollowingUser: boolean
  isUnfollowingUser: boolean
  unfollowingUserId: string | null

  // Actions
  loadSocialData: () => Promise<void>
  loadOnlineUsers: () => Promise<void>
  loadFollowers: (page?: number) => Promise<void>
  loadFollowing: (page?: number) => Promise<void>
  loadMutualFollows: (page?: number) => Promise<void>
  handleSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  searchUsers: (query: string) => Promise<void>
  followUser: (userId: string) => Promise<void>
  unfollowUser: (userId: string) => Promise<void>
  addUserOnline: (userId: string) => void
  removeUserOffline: (userId: string) => void
  addFollower: (user: User) => void
  removeFollower: (userId: string) => void
  addFollowing: (user: User) => void
  removeFollowing: (userId: string) => void
  getAllSocialUsers: () => User[]
}

export const useZeefSocial = (): UseZeefSocialReturn => {
  const [followers, setFollowers] = useState<User[]>([])
  const [following, setFollowing] = useState<User[]>([])
  const [mutualFollows, setMutualFollows] = useState<User[]>([])
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set())
  
  const [loading, setLoading] = useState<SocialLoadingState>({
    followers: false,
    following: false,
    mutual: false,
  })
  
  const [cursors, setCursors] = useState<SocialCursors>({
    followers: { page: 1, hasMore: true },
    following: { page: 1, hasMore: true },
    mutual: { page: 1, hasMore: true },
  })

  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isFollowingUser, setIsFollowingUser] = useState(false)
  const [isUnfollowingUser, setIsUnfollowingUser] = useState(false)
  const [unfollowingUserId, setUnfollowingUserId] = useState<string | null>(null)

  const loadSocialData = useCallback(async () => {
    await Promise.all([loadFollowers(), loadFollowing(), loadMutualFollows()])
  }, [])

  const loadOnlineUsers = useCallback(async () => {
    try {
      const users = await ZeefService.getOnlineUsers()
      const onlineUserIds = new Set(users.map((user) => user.id))
      setOnlineUsers(onlineUserIds)
    } catch (error) {
      console.error("Failed to load online users:", error)
      setOnlineUsers(new Set())
    }
  }, [])

  const loadFollowers = useCallback(async (page = 1) => {
    if (loading.followers) return

    try {
      setLoading(prev => ({ ...prev, followers: true }))

      const response = await ZeefService.getFollowers({ page, limit: 20 })

      setFollowers(prev => 
        page === 1 ? response.items : [...prev, ...response.items]
      )
      setCursors(prev => ({
        ...prev,
        followers: { page: response.page, hasMore: response.has_next },
      }))
      setLoading(prev => ({ ...prev, followers: false }))
    } catch (error) {
      console.error("Failed to load followers:", error)
      setLoading(prev => ({ ...prev, followers: false }))
    }
  }, [loading.followers])

  const loadFollowing = useCallback(async (page = 1) => {
    if (loading.following) return

    try {
      setLoading(prev => ({ ...prev, following: true }))

      const response = await ZeefService.getFollowing({ page, limit: 20 })

      setFollowing(prev => 
        page === 1 ? response.items : [...prev, ...response.items]
      )
      setCursors(prev => ({
        ...prev,
        following: { page: response.page, hasMore: response.has_next },
      }))
      setLoading(prev => ({ ...prev, following: false }))
    } catch (error) {
      console.error("Failed to load following:", error)
      setLoading(prev => ({ ...prev, following: false }))
    }
  }, [loading.following])

  const loadMutualFollows = useCallback(async (page = 1) => {
    if (loading.mutual) return

    try {
      setLoading(prev => ({ ...prev, mutual: true }))

      const response = await ZeefService.getMutualFollows({ page, limit: 20 })

      setMutualFollows(prev => 
        page === 1 ? response.items : [...prev, ...response.items]
      )
      setCursors(prev => ({
        ...prev,
        mutual: { page: response.page, hasMore: response.has_next },
      }))
      setLoading(prev => ({ ...prev, mutual: false }))
    } catch (error) {
      console.error("Failed to load mutual follows:", error)
      setLoading(prev => ({ ...prev, mutual: false }))
    }
  }, [loading.mutual])

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value
    setSearchQuery(query)

    if (query.trim().length >= 2) {
      searchUsers(query.trim())
    } else {
      setSearchResults([])
    }
  }, [])

  const searchUsers = useCallback(async (query: string) => {
    if (isSearching) return

    try {
      setIsSearching(true)
      const response = await ZeefService.searchUsers({ query, page: 1, limit: 10 })
      setSearchResults(response.items)
      setIsSearching(false)
    } catch (error) {
      console.error("Failed to search users:", error)
      setSearchResults([])
      setIsSearching(false)
    }
  }, [isSearching])

  const followUser = useCallback(async (userId: string) => {
    if (isFollowingUser) return

    try {
      setIsFollowingUser(true)
      await ZeefService.followUser(userId)
      await loadSocialData()
      setSearchQuery("")
      setSearchResults([])
      setIsFollowingUser(false)
    } catch (error) {
      console.error("Failed to follow user:", error)
      setIsFollowingUser(false)
    }
  }, [isFollowingUser, loadSocialData])

  const unfollowUser = useCallback(async (userId: string) => {
    if (isUnfollowingUser) return

    try {
      setIsUnfollowingUser(true)
      setUnfollowingUserId(userId)
      await ZeefService.unfollowUser(userId)
      await loadSocialData()
      setIsUnfollowingUser(false)
      setUnfollowingUserId(null)
    } catch (error) {
      console.error("Failed to unfollow user:", error)
      setIsUnfollowingUser(false)
      setUnfollowingUserId(null)
    }
  }, [isUnfollowingUser, loadSocialData])

  // WebSocket event handlers
  const addUserOnline = useCallback((userId: string) => {
    setOnlineUsers(prev => new Set([...prev, userId]))
  }, [])

  const removeUserOffline = useCallback((userId: string) => {
    setOnlineUsers(prev => {
      const newSet = new Set(prev)
      newSet.delete(userId)
      return newSet
    })
  }, [])

  const addFollower = useCallback((user: User) => {
    setFollowers(prev => [user, ...prev])
  }, [])

  const removeFollower = useCallback((userId: string) => {
    setFollowers(prev => prev.filter(user => user.id !== userId))
  }, [])

  const addFollowing = useCallback((user: User) => {
    setFollowing(prev => [user, ...prev])
  }, [])

  const removeFollowing = useCallback((userId: string) => {
    setFollowing(prev => prev.filter(user => user.id !== userId))
  }, [])

  const getAllSocialUsers = useCallback((): User[] => {
    const allUsers = [...following, ...followers, ...mutualFollows]
    // Remove duplicates by user ID
    return Array.from(new Map(allUsers.map(user => [user.id, user])).values())
  }, [following, followers, mutualFollows])

  return {
    // State
    followers,
    following,
    mutualFollows,
    onlineUsers,
    loading,
    cursors,
    searchQuery,
    searchResults,
    isSearching,
    isFollowingUser,
    isUnfollowingUser,
    unfollowingUserId,

    // Actions
    loadSocialData,
    loadOnlineUsers,
    loadFollowers,
    loadFollowing,
    loadMutualFollows,
    handleSearchInputChange,
    searchUsers,
    followUser,
    unfollowUser,
    addUserOnline,
    removeUserOffline,
    addFollower,
    removeFollower,
    addFollowing,
    removeFollowing,
    getAllSocialUsers,
  }
}
